import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { useLocation } from 'react-router-dom';
import { fr as frLocale } from 'date-fns/locale/fr';
import { getEndOfWeekDate, getNumericDate, getStartOfWeekDate, getWeekNumber } from 'utils/dateTimeFormat';
import { SLabel, SLabelWrapper, SPicker, SWrapper } from './styles';
import ChevronDown from 'images/icons/ChevronDown.svg?react';
import ChevronUp from 'images/icons/ChevronUp.svg?react';
import 'react-datepicker/dist/react-datepicker.css';
import { useDateRangeStore } from 'stores/dateRange';

registerLocale('fr', frLocale);

const WeekPicker: FC = () => {
    const { t } = useTranslation();
    const currentWeek = getWeekNumber(new Date());
    const location = useLocation();
    const [label, setLabel] = useState<string>('');
    const [dateSelected, setDateSelected] = useState<Date>(new Date());
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const setDateRange = useDateRangeStore((s) => s.setDateRange);

    useEffect(() => {
        const startDate = getStartOfWeekDate(dateSelected);
        const endDate = getEndOfWeekDate(dateSelected);

        setDateRange({
            endDate,
            startDate,
        });

        if (getWeekNumber(dateSelected) === currentWeek) {
            setLabel(t('common.this-week'));
        } else {
            setLabel(
                t('common.week-from-to', {
                    end: getNumericDate(endDate),
                    start: getNumericDate(startDate),
                }),
            );
        }
        setIsOpen(false);
    }, [dateSelected]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const defaultTime = queryParams.get('time');
        if (defaultTime) {
            const defaultDate = new Date();
            defaultDate.setTime(Number(defaultTime));
            setDateSelected(defaultDate);
        }
    }, []);

    return (
        <SWrapper>
            <button onClick={() => setIsOpen(!isOpen)} type="button">
                <SLabelWrapper>
                    <SLabel>{label}</SLabel>
                    {isOpen ? <ChevronUp /> : <ChevronDown />}
                </SLabelWrapper>
            </button>
            {isOpen && (
                <SPicker>
                    <ReactDatePicker
                        calendarStartDay={1}
                        endDate={dateSelected ? getEndOfWeekDate(dateSelected) : undefined}
                        locale="fr"
                        onCalendarClose={() => setIsOpen(false)}
                        onCalendarOpen={() => setIsOpen(true)}
                        onChange={(date: Date | null) => date && setDateSelected(date)}
                        startDate={dateSelected ? getStartOfWeekDate(dateSelected) : undefined}
                        inline
                    />
                </SPicker>
            )}
        </SWrapper>
    );
};

export default WeekPicker;
